import React, { useEffect, useState } from 'react';
import AppInput from '../../../../../base/input';
import { useFormik } from 'formik';
import { useBeneficiaryStore } from '../../../../../../store';
import { FiCheck } from 'react-icons/fi';
import AppButton from '../../../../../base/button';
import beneficiarySchemas from '../../../../../../constants/schemas/beneficiary';
import Loader from '../../../../../base/loader';
import toast from 'react-hot-toast';
import usePayoutStore from '../../../../../../store/payout';

interface AddSwiftUnknownBeneficiaryProps {
  addBeneficiary: () => void;
  prev: () => void;
  loading: boolean;
  caller?: string;
}

const AddSwiftUnknwonBeneficiary: React.FC<AddSwiftUnknownBeneficiaryProps> = ({
  addBeneficiary,
  prev,
  loading,
  caller,
}) => {
  const {
    setBeneficiaryPayload,
    beneficiaryPayload,
    validateSwiftCode,
    loading: validating,
  } = useBeneficiaryStore();
  const { beneficiaryFormState } = usePayoutStore();
  const [isBicCodeValid, setIsBicCodeValid] = useState(false);

  const formik = useFormik({
    initialValues: {
      bic: beneficiaryPayload.unKnownPayment?.bic ?? '',
      accountNumber: beneficiaryPayload?.unKnownPayment.accountNumber ?? '',
    },
    validationSchema: beneficiarySchemas[5],
    validateOnMount: true,
    onSubmit() {},
  });

  const disableForm = () => {
    return (
      !formik.values.bic || !formik.values.accountNumber || !isBicCodeValid
    );
  };

  useEffect(() => {
    setBeneficiaryPayload({
      ...beneficiaryPayload,
      unKnownPayment: {
        bic: formik.values.bic,
        accountNumber: formik.values.accountNumber,
      },
    });
  }, [formik.values]);

  const submitForm = () => {
    addBeneficiary();
  };

  return (
    <div>
      <form
        onSubmit={formik.handleSubmit}
        action="#"
        className="flex flex-col gap-8"
      >
        <div>
          {caller !== 'payout' && (
            <div className="flex items-center text-sm font-semibold mb-6">
              <span className="mr-4 text-gray-500">
                Swift Unknown Account Details
              </span>
              <div className="flex-1 h-px bg-gray-300"></div>
            </div>
          )}

          <div className="flex flex-col gap-8">
            <div className="grid md:grid-cols-2 grid-cols-1 gap-6">
              <AppInput
                type="text"
                name="bic"
                id="bic"
                label="BIC/SWIFT Code"
                onChange={formik.handleChange}
                onBlur={async (e) => {
                  formik.handleBlur(e);
                  if (formik.values.bic) {
                    const response = await validateSwiftCode(e.target.value);
                    if (response && response.isSuccess) {
                      setIsBicCodeValid(true);
                      setBeneficiaryPayload({
                        ...beneficiaryPayload,
                        bankName: response.value.bankName,
                      });
                      toast.success('Valid Swift/BIC Code entered.');
                    } else {
                      setIsBicCodeValid(false);
                      toast.error('Swift/BIC Code entered is invalid.');
                    }
                  }
                }}
                value={formik.values.bic}
                placeholder="Enter BIC"
                required
                endIcon={
                  validating ? (
                    <Loader width="1.2rem" borderTopColor="border-t-blue-500" />
                  ) : isBicCodeValid ? (
                    <FiCheck />
                  ) : null
                }
                errorMessage={
                  formik.touched.bic && formik.errors.bic
                    ? formik.errors.bic
                    : ''
                }
                disabled={
                  caller === 'payout' &&
                  beneficiaryFormState === 'selected_existing'
                }
              />

              <AppInput
                type="text"
                name="accountNumber"
                id="account-number"
                label="Account Number"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.accountNumber}
                placeholder="Enter Account Number"
                required
                errorMessage={
                  formik.touched.accountNumber && formik.errors.accountNumber
                    ? formik.errors.accountNumber
                    : ''
                }
                disabled={
                  caller === 'payout' &&
                  beneficiaryFormState === 'selected_existing'
                }
              />
            </div>
          </div>
        </div>
        {caller !== 'payout' && (
          <div className="flex gap-4 mt-8">
            <AppButton label="Previous" type="light" clicked={prev} />
            <AppButton
              label="Add Beneficiary"
              type="primary"
              clicked={() => submitForm()}
              disabled={disableForm()}
              loading={loading}
            />
          </div>
        )}
      </form>
    </div>
  );
};

export default AddSwiftUnknwonBeneficiary;
